import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Space, Spin, Table, Tag, Typography } from 'antd';
import { ReloadOutlined, InboxOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { acceptOrderAverageCost, fetchOrderWarehouseSummary } from '../api/orderWarehouseApi';
import type { OrderWarehouseLine, OrderWarehouseSummary } from '../../../types/orderWarehouseSummary';
import { formatUnit } from '../../../utils/units';
import { formatCurrency, formatCurrencyUnitPrice } from '../../../utils/currency';
import { useFeedback } from '../../../utils/feedback';
import './OrderWarehouseSection.css';

interface Props {
  orderId: string;
  canViewMargin: boolean;
  refreshKey?: unknown;
  onRefreshOrder?: () => Promise<unknown>;
}

export default function OrderWarehouseSection({ orderId, canViewMargin, refreshKey, onRefreshOrder }: Props) {
  const { t } = useTranslation(['orders', 'common', 'wz']);
  const feedback = useFeedback();
  const [revision, setRevision] = useState(0);
  const [accepting, setAccepting] = useState(false);
  const [state, setState] = useState<{ orderId: string; summary?: OrderWarehouseSummary; failed?: boolean }>();
  useEffect(() => {
    let active = true;
    setState(undefined);
    fetchOrderWarehouseSummary(orderId).then(
      summary => { if (active) setState({ orderId, summary }); },
      () => { if (active) setState({ orderId, failed: true }); },
    );
    return () => { active = false; };
  }, [orderId, revision, refreshKey]);
  // A return from the PZ/catalog screen must refresh the live valuation.
  useEffect(() => {
    const refresh = () => {
      if (!document.hidden) {
        if (onRefreshOrder) void onRefreshOrder().catch(() => setRevision(value => value + 1));
        else setRevision(value => value + 1);
      }
    };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => { window.removeEventListener('focus', refresh); document.removeEventListener('visibilitychange', refresh); };
  }, [onRefreshOrder]);
  const summary = state?.orderId === orderId ? state.summary : undefined;
  const failed = state?.orderId === orderId && state.failed;
  const plan = canViewMargin ? summary?.finance?.plan : undefined;
  const needsAverage = plan && (plan.usesAverage || plan.lines.some(line => line.costPending));
  const noStock = !!summary?.lines.some(line => (line.remainingQuantity ?? 0) > 0) && summary.lines.every(line => line.availableQuantity === 0);
  const quantity = (value: number | null, line: OrderWarehouseLine) => value === null
    ? t('orders:warehouseFlow.unknownAvailability')
    : `${value.toLocaleString('pl-PL', { maximumFractionDigits: 4 })} ${formatUnit(line.unit, 'pl')}`;
  const amount = (value: number | null | undefined) => value == null ? '—' : formatCurrency(value, 'PLN');
  const acceptAverage = async () => {
    setAccepting(true);
    try {
      await acceptOrderAverageCost(orderId);
      setRevision(value => value + 1);
      await onRefreshOrder?.();
      feedback.success(t('orders:warehouseFlow.averageSaved'));
    } catch {
      feedback.error(t('orders:warehouseFlow.averageError'));
    } finally { setAccepting(false); }
  };
  return <Card className="order-warehouse" title={<Space><InboxOutlined />{t('orders:warehouseFlow.fulfillment')}</Space>}
    extra={<Button type="text" icon={<ReloadOutlined />} aria-label={t('orders:warehouseFlow.refresh')}
      onClick={() => setRevision(value => value + 1)} />}>
    <div className="order-warehouse-intro">
      <Typography.Text type="secondary">{t('orders:warehouseFlow.fulfillmentHelp')}</Typography.Text>
      {noStock && <Tag>{t('orders:warehouseFlow.noStock')}</Tag>}
    </div>
    {failed ? <Alert type="warning" showIcon message={t('orders:warehouseFlow.loadError')}
      action={<Button onClick={() => setRevision(value => value + 1)}>{t('common:retry')}</Button>} />
      : !summary ? <Spin aria-label={t('common:messages.loading')} /> : <>
        <Table<OrderWarehouseLine> dataSource={summary.lines} rowKey="id" size="small" pagination={false}
          scroll={{ x: 650 }} columns={[
            { title: t('orders:warehouseFlow.product'), dataIndex: 'name', key: 'name' },
            ...(['orderedQuantity', 'availableQuantity', 'issuedQuantity', 'remainingQuantity', 'invoicedQuantity'] as const).map(key => ({
              title: t(`orders:warehouseFlow.${key}`), key, align: 'right' as const,
              render: (_: unknown, line: OrderWarehouseLine) => <span className="order-warehouse-quantity">{quantity(line[key], line)}</span>,
            })),
          ]} />
        {summary.documents.length > 0 && <Space wrap className="order-warehouse-documents">
          {summary.documents.map(document => <Link key={document.id} to={`/app/wz/${encodeURIComponent(document.id)}`}>
            {document.wzNumber}
          </Link>)}
        </Space>}
        {canViewMargin && summary.finance && <section className="order-warehouse-valuation">
          <div className="order-warehouse-valuation-heading">
            <Typography.Title level={5}>{t('orders:warehouseFlow.planningTitle')}</Typography.Title>
            {plan?.usesAverage && <Tag color="gold">{t('orders:warehouseFlow.estimated')}</Tag>}
          </div>
          {plan && needsAverage && <>
            <Typography.Paragraph type="secondary">{t(plan.averageAccepted ? 'orders:warehouseFlow.averageActive' : 'orders:warehouseFlow.averageProposal')}</Typography.Paragraph>
            <div className="order-warehouse-average-lines">
              {plan.lines.filter(line => line.averageCostPLN != null || line.costPending).map(line => <div className="order-warehouse-average-line" key={line.id}>
                <span>{summary.lines.find(product => product.id === line.id)?.name}</span>
                <span className="order-warehouse-average-price">{t('orders:warehouseFlow.averagePrice')}: {line.averagePurchasePricePLN == null ? '—' : formatCurrencyUnitPrice(line.averagePurchasePricePLN, 'PLN', 'pl')}
                  {line.averagePurchasePriceUnit && ` / ${formatUnit(line.averagePurchasePriceUnit, 'pl')}`}</span>
                <strong>{amount(line.averageCostPLN)}</strong>
              </div>)}
            </div>
          </>}
          <dl className="order-warehouse-costs">
            {plan != null && plan.stockCostPLN > 0 && <div><dt>{t('orders:warehouseFlow.availableStockCost')}</dt><dd>{amount(plan.stockCostPLN)}</dd></div>}
            <div><dt title={t('orders:warehouseFlow.fifoCostHelp')}>{t('orders:warehouseFlow.fifoCost')}</dt><dd>{amount(summary.finance.knownFifoCostPLN)}</dd></div>
            <div><dt>{t('orders:warehouseFlow.transportCost')}</dt><dd>{amount(summary.finance.transportCostPLN)}</dd></div>
            <div><dt>{t('orders:warehouseFlow.otherCost')}</dt><dd>{amount(summary.finance.otherCostPLN)}</dd></div>
            {plan?.usesAverage && <div><dt>{t('orders:warehouseFlow.averageTotal')}</dt><dd>{amount(plan.averageCostPLN)}</dd></div>}
          </dl>
          {plan ? <>
            {plan.costPending && <Alert type="warning" showIcon message={t('orders:warehouseFlow.averageMissing')} />}
            {needsAverage && !plan.averageAccepted && <div className="order-warehouse-accept">
              <Button type="primary" loading={accepting} disabled={plan.costPending} onClick={acceptAverage}>{t('orders:warehouseFlow.acceptAverage')}</Button>
              <Typography.Text type="secondary">{t('orders:warehouseFlow.plannedCost')}: <strong>{amount(plan.candidateCostPLN)}</strong></Typography.Text>
            </div>}
            {plan.totalCostPLN != null && <div className="order-warehouse-result">
              <div><span>{t('orders:warehouseFlow.plannedCost')}</span><strong>{amount(plan.totalCostPLN)}</strong></div>
              <div><span>{t('orders:warehouseFlow.plannedMargin')}</span><strong>{amount(plan.marginPLN)}
                {plan.marginPercent != null && <small> ({plan.marginPercent.toLocaleString('pl-PL')}%)</small>}</strong></div>
              {plan.usesAverage && <Tag color="gold">{t('orders:warehouseFlow.averageAccepted')}</Tag>}
            </div>}
            {!plan.usesAverage && <Typography.Text type="secondary">{t('orders:warehouseFlow.stockPlanHelp')}</Typography.Text>}
          </> : summary.finance.costPending && <Alert type="warning" showIcon message={t('orders:warehouseFlow.costPending')} />}
        </section>}
      </>}
    <div className="order-warehouse-actions">
      <Link to={`/app/wz/new?orderId=${encodeURIComponent(orderId)}`}>
        <Button type={noStock ? 'default' : 'primary'}>{t('orders:warehouseFlow.issueGoods')}</Button>
      </Link>
    </div>
  </Card>;
}
